import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { membershipApi } from '../api/membershipApi';
import { profileApi } from '../api/profileApi';
import { useProfile } from '../context/ProfileContext';
import { completeMobileReturn } from '../mobileReturn';
import { Membership, ProductOffering } from '../types';

const formatUSD = (value: number): string =>
  value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;

const formatDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const byDisplayOrder = (left: ProductOffering, right: ProductOffering): number =>
  left.displayOrder - right.displayOrder || left.name.localeCompare(right.name);

const ProfileMembership: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, refreshProfile, logout } = useProfile();
  const toast = useRef<Toast>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [products, setProducts] = useState<ProductOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [billingSyncFailed, setBillingSyncFailed] = useState(false);
  const [billingRetry, setBillingRetry] = useState(0);
  const membershipRequest = useRef(0);
  const cancellationStarting = useRef(false);
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);

  const load = useCallback(async () => {
    const request = ++membershipRequest.current;
    setLoading(true);
    try {
      const [current, catalog] = await Promise.all([
        membershipApi.get(profile.userId),
        membershipApi.getProducts(),
      ]);
      if (request === membershipRequest.current) setMembership(current);
      setProducts(catalog.sort(byDisplayOrder));
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: "Couldn't load membership",
        detail: 'Please try again in a moment.',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [profile.userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const billing = query.get('billing')
      ?? (sessionStorage.getItem('caddiesight.billing.pending') === profile.userId ? 'return' : null);
    if (billing !== 'return' && billing !== 'complete') return;
    let cancelled = false;
    setWorking(true);
    setBillingSyncFailed(false);
    (async () => {
      try {
        const updated = await membershipApi.syncBilling(profile.userId);
        if (cancelled) return;
        ++membershipRequest.current;
        setMembership(updated);
        await refreshProfile();
        if (cancelled) return;
        sessionStorage.removeItem('caddiesight.billing.pending');
        cancellationStarting.current = false;
        query.delete('billing');
        navigate({ pathname: location.pathname, search: query.toString(), hash: location.hash }, { replace: true });
        if (billing === 'complete' && (updated.cancelAtPeriodEnd || updated.price === 0)) {
          completeMobileReturn();
        }
      } catch {
        if (!cancelled) setBillingSyncFailed(true);
      } finally {
        if (!cancelled) {
          setWorking(false);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [billingRetry, location.hash, location.pathname, location.search, navigate, profile.userId, refreshProfile]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && sessionStorage.getItem('caddiesight.billing.pending') === profile.userId) {
        cancellationStarting.current = false;
        const query = new URLSearchParams(location.search);
        query.set('billing', 'return');
        navigate({ pathname: location.pathname, search: query.toString() }, { replace: true });
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [location.pathname, location.search, navigate, profile.userId]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const checkout = query.get('checkout');
    const sessionId = query.get('session_id');
    if (checkout === 'canceled') {
      sessionStorage.removeItem('caddiesight.checkout.returnTo');
      navigate('/profile', { replace: true });
      return;
    }
    if (checkout !== 'success' || !sessionId) return;

    let cancelled = false;
    (async () => {
      setWorking(true);
      let checkoutConfirmed = false;
      try {
        const updated = await membershipApi.confirmCheckout(profile.userId, sessionId);
        if (cancelled) return;
        checkoutConfirmed = true;
        setMembership(updated);
        await refreshProfile();
        await load();
        toast.current?.show({
          severity: 'success',
          summary: `You're now on ${updated.planName}`,
          detail: 'Your Stripe payment was confirmed.',
          life: 4000,
        });
      } catch {
        if (!cancelled) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Payment is processing',
            detail: 'Your membership will update as soon as Stripe confirms the payment.',
            life: 5000,
          });
        }
      } finally {
        if (!cancelled) {
          setWorking(false);
          const returnTo = sessionStorage.getItem('caddiesight.checkout.returnTo');
          sessionStorage.removeItem('caddiesight.checkout.returnTo');
          if (!checkoutConfirmed || !completeMobileReturn()) {
            navigate(checkoutConfirmed && returnTo === '/play' ? '/play' : '/profile', { replace: true });
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load, location.search, navigate, profile.userId, refreshProfile]);

  const clubProduct = products.find((product) => product.price > 0) ?? null;
  const isClubMember = (membership?.price ?? 0) > 0;
  // A promo-campaign trial is Club for free: it has no price and no billing to manage, and it ends on its own.
  const isFreeTrial = membership?.status === 'Trialing' && !isClubMember;
  const canChangeToRange = isClubMember && membership?.source.toLowerCase() === 'stripe' && !membership.cancelAtPeriodEnd;
  const renewsAt = formatDate(membership?.renewsAt);

  const joinClub = async () => {
    if (!clubProduct) return;
    setWorking(true);
    try {
      sessionStorage.setItem('caddiesight.checkout.returnTo', '/profile');
      const checkoutUrl = await membershipApi.startCheckout(profile.userId, clubProduct.id);
      window.location.assign(checkoutUrl);
    } catch {
      sessionStorage.removeItem('caddiesight.checkout.returnTo');
      setWorking(false);
      toast.current?.show({
        severity: 'error',
        summary: "Couldn't start checkout",
        detail: 'Please try again in a moment.',
        life: 4000,
      });
    }
  };

  const changeToRange = async () => {
    if (cancellationStarting.current) return;
    cancellationStarting.current = true;
    setWorking(true);
    try {
      const url = await membershipApi.startCancellation(profile.userId);
      sessionStorage.setItem('caddiesight.billing.pending', profile.userId);
      window.location.assign(url);
    } catch {
      cancellationStarting.current = false;
      setWorking(false);
      toast.current?.show({
        severity: 'error',
        summary: "Couldn't open Stripe cancellation",
        detail: 'Please try again or contact support@caddiesight.com.',
        life: 4500,
      });
    }
  };

  const deleteAccount = async () => {
    setWorking(true);
    try {
      await profileApi.deleteAccount(profile.userId);
      setDeleteStep(0);
      logout();
      navigate('/login', { replace: true });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: "Couldn't delete account",
        detail: 'Your account remains active. Please try again or contact support.',
        life: 5000,
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <Dialog
        visible={deleteStep === 1}
        onHide={() => !working && setDeleteStep(0)}
        showHeader={false}
        dismissableMask
        className="confirm-modal"
        aria-label="Delete account"
      >
        <div className="confirm-modal__inner">
          <span className="confirm-modal__icon confirm-modal__icon--danger" aria-hidden="true">
            <i className="pi pi-trash" />
          </span>
          <h2 className="confirm-modal__title">Delete account?</h2>
          <p className="confirm-modal__message">
            Deleting your account is permanent and cannot be undone.
          </p>
          <ul className="confirm-modal__list confirm-modal__list--danger">
            <li><i className="pi pi-angle-right" aria-hidden="true" /> Any active subscription is canceled first</li>
            <li><i className="pi pi-angle-right" aria-hidden="true" /> Your profile is closed and marked as deleted</li>
            <li><i className="pi pi-angle-right" aria-hidden="true" /> You'll be signed out and can't sign back in</li>
          </ul>
          <div className="confirm-modal__actions">
            <Button label="Keep account" text severity="secondary" onClick={() => setDeleteStep(0)} />
            <Button label="Continue" severity="danger" onClick={() => setDeleteStep(2)} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={deleteStep === 2}
        onHide={() => !working && setDeleteStep(0)}
        showHeader={false}
        dismissableMask={!working}
        className="confirm-modal"
        aria-label="Permanently delete account"
      >
        <div className="confirm-modal__inner">
          <span className="confirm-modal__icon confirm-modal__icon--danger" aria-hidden="true">
            <i className="pi pi-exclamation-triangle" />
          </span>
          <h2 className="confirm-modal__title">Permanently delete account</h2>
          <p className="confirm-modal__message">
            This action is irreversible. You'll be signed out, and this CaddieSight account cannot
            be restored or used again.
          </p>
          <div className="confirm-modal__actions">
            <Button label="Back" text severity="secondary" disabled={working} onClick={() => setDeleteStep(1)} />
            <Button label="Delete account" severity="danger" loading={working} onClick={deleteAccount} />
          </div>
        </div>
      </Dialog>

      <div className="profile-membership">
        <div className="membership-summary">
          <div className="membership-summary__info">
            <i className="pi pi-star-fill membership-summary__icon" aria-hidden="true" />
            <div>
              <span className="membership-summary__label">Membership</span>
              <span className="membership-summary__plan">
                {loading ? 'Loading membership' : membership?.planName ?? 'Range Membership'}
              </span>
              {!loading && membership && (
                <span className="membership-summary__meta">
                  {isFreeTrial
                    ? `Free trial${renewsAt ? ` · Ends ${renewsAt}` : ''}`
                    : isClubMember ? `${formatUSD(membership.price)}/month` : 'Free'}
                  {isClubMember && renewsAt
                    ? membership.cancelAtPeriodEnd ? ` · Changes to Range on ${renewsAt}` : ` · Renews ${renewsAt}`
                    : ''}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="profile-membership__actions">
          {billingSyncFailed && (
            <div role="alert">
              <p>Billing status could not be refreshed.</p>
              <Button label="Retry billing refresh" icon="pi pi-refresh" outlined disabled={working}
                onClick={() => setBillingRetry((retry) => retry + 1)} />
            </div>
          )}
          {!loading && membership && !isClubMember && !isFreeTrial && clubProduct && (
            <Button
              label={`Join Club Membership · ${formatUSD(clubProduct.price)}/month`}
              icon="pi pi-credit-card"
              loading={working}
              disabled={working || billingSyncFailed}
              onClick={joinClub}
            />
          )}
          {!loading && canChangeToRange && (
            <Button
              label="Cancel Club Membership"
              icon="pi pi-arrow-down"
              severity="secondary"
              outlined
              disabled={working || billingSyncFailed}
              loading={working && cancellationStarting.current}
              onClick={changeToRange}
            />
          )}
          <Button
            label="Delete account"
            icon="pi pi-trash"
            severity="danger"
            text
            disabled={working}
            onClick={() => setDeleteStep(1)}
          />
        </div>
      </div>
    </>
  );
};

export default ProfileMembership;